import { Label as LabelPrimitive } from 'radix-ui'
import { type ComponentProps, type ReactNode, useId } from 'react'
import { cn } from '../lib/utils'

const fieldBase = [
  'w-full min-w-0 rounded-lg border border-input bg-surface px-3 text-sm text-foreground shadow-soft',
  'placeholder:text-muted-foreground/70 transition-[border-color,box-shadow] duration-150',
  'focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/20',
  'disabled:cursor-not-allowed disabled:opacity-60',
  'aria-invalid:border-danger aria-invalid:ring-danger/15',
]

export function Input({ className, type = 'text', ...props }: ComponentProps<'input'>) {
  return <input type={type} className={cn(fieldBase, 'h-9 py-1', className)} {...props} />
}

export function Textarea({ className, ...props }: ComponentProps<'textarea'>) {
  return <textarea className={cn(fieldBase, 'min-h-20 py-2', className)} {...props} />
}

export function Label({ className, ...props }: ComponentProps<typeof LabelPrimitive.Root>) {
  return (
    <LabelPrimitive.Root
      className={cn('text-[13px] leading-none font-medium text-foreground select-none', className)}
      {...props}
    />
  )
}

interface FieldProps {
  label: ReactNode
  error?: string | undefined
  hint?: ReactNode
  className?: string
  /** Render prop receives the generated id plus aria wiring for the control. */
  children: (control: { id: string; 'aria-invalid'?: true; 'aria-describedby'?: string }) => ReactNode
  aside?: ReactNode
}

/** Label + control + hint/error, with ids and aria wired up. */
export function Field({ label, error, hint, className, children, aside }: FieldProps) {
  const id = useId()
  const messageId = `${id}-message`
  const hasMessage = Boolean(error ?? hint)

  return (
    <div className={cn('grid gap-1.5', className)}>
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={id}>{label}</Label>
        {aside}
      </div>
      {children({
        id,
        ...(error ? { 'aria-invalid': true as const } : {}),
        ...(hasMessage ? { 'aria-describedby': messageId } : {}),
      })}
      {error ? (
        <p id={messageId} role="alert" className="text-xs font-medium text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={messageId} className="text-xs text-muted-foreground">
          {hint}
        </p>
      ) : null}
    </div>
  )
}
